import { CircleDollarSign, Eye, Pencil, Plus, ReceiptText, Trash2 } from 'lucide-react';
import type { ReactElement } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { cajaBase } from '@/shared/lib/cajasEfectivo';
import type { ComprobantePagoItem } from '@/shared/api/controllers/comprobantePagos';
import { fmtPct } from '@/shared/api/controllers/mpCuotas';
import { ESTADO_ECHEQ_LABEL, type Echeq } from '@/shared/api/controllers/echeqs';
import type { Moneda } from './MoneyCell';
import { MoneyCell } from './MoneyCell';
import { PILL_BASE, StatusCPBadge } from './StatusCPBadge';

/**
 * "Carga de pagos" table — renders the active `04.ComprobantePagos` rows
 * for a venta plus the two register buttons on the header. Per-row
 * actions: view (stub for now) and anular.
 */
export function ComprobantePagosTable({
  comprobantes,
  moneda,
  homeCurrency,
  onRegistrarPago,
  onRegistrarPromesa,
  onVerComprobante,
  onAnularComprobante,
  onCargarComprobante,
  onEditarPromesa,
  onSolicitarFacturacionPrevia,
  previaEstadoByComprobante,
  previaMotivoRechazoByComprobante,
  echeqByComprobante,
  recargoMp = null,
  readOnly = false,
  registrarDisabledReason,
  summary,
}: {
  comprobantes: ComprobantePagoItem[];
  moneda: Moneda;
  /** Active country's primary currency (ARS/MXN). Used to label the
   *  native-currency amount on cross-currency pagos (e.g. a USD medio
   *  on an ARS venta, or the reverse). */
  homeCurrency: Moneda;
  onRegistrarPago: () => void;
  onRegistrarPromesa: () => void;
  onVerComprobante: (c: ComprobantePagoItem) => void;
  onAnularComprobante: (c: ComprobantePagoItem) => void;
  /** Opens the "Subir Comprobante" dialog. Only fires for `Pendiente
   *  de Cobro` rows. */
  onCargarComprobante: (c: ComprobantePagoItem) => void;
  /** Opens the "Editar Pago" dialog. Only fires for `Pendiente de
   *  Cobro` rows. */
  onEditarPromesa: (c: ComprobantePagoItem) => void;
  /** AR: pedir autorización para facturar este cobro antes de cobrarlo.
   *  Ausente fuera de AR — la acción no se renderiza. */
  onSolicitarFacturacionPrevia?: (c: ComprobantePagoItem) => void;
  /** id de comprobante → estado de su última solicitud de facturación previa. */
  previaEstadoByComprobante?: Map<string, string>;
  /** id de comprobante → motivo con el que se la rechazaron. */
  previaMotivoRechazoByComprobante?: Map<string, string>;
  /** id de comprobante → su E-cheq (AR). */
  echeqByComprobante?: Map<string, Echeq>;
  /** Recargo MercadoPago de la venta y su caja: se marca en esos cobros. */
  recargoMp?: { caja: string; cuotas: number; pct: number } | null;
  readOnly?: boolean;
  /** When set, Registrar pago/promesa buttons are disabled with this
   *  reason shown as a tooltip. */
  registrarDisabledReason?: string;
  /** Optional totals + progress data rendered as a card footer.
   *  Pagado/total drive a progress bar that mirrors the medios
   *  Cobertura strip but measures actual cobro state instead of
   *  planned distribution. */
  summary?: {
    disponibleAFacturar: number;
    pagado: number;
    pendiente: number;
    total: number;
  };
}) {
  const disabled = readOnly || Boolean(registrarDisabledReason);
  const disabledTitle = readOnly ? undefined : registrarDisabledReason;
  const progressPct =
    summary && summary.total > 0
      ? Math.max(0, Math.min(100, Math.round((summary.pagado / summary.total) * 100)))
      : null;
  return (
    // QA: card uses internal scroll for the table body so the page
    // doesn't need an outer scroll. `min-h-0` is required for flex
    // children to shrink below content size; `overscroll-contain`
    // stops macOS rubber-band scrolling from leaking to the page.
    // `h-full` lets the card fill whatever space its parent gave it.
    <div className="flex min-w-0 flex-col gap-2 overflow-hidden rounded-2xl border bg-card p-4 lg:h-full lg:min-h-0">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <h2 className="text-base font-semibold tracking-tight text-foreground">
            Carga de pagos
          </h2>
          {registrarDisabledReason && !readOnly && (
            <span className="text-xs text-muted-foreground">
              {registrarDisabledReason}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Tip label={disabledTitle ?? 'Registrar pago'} disabled={disabled}>
            <Button
              type="button"
              size="sm"
              onClick={onRegistrarPago}
              disabled={disabled}
              className="gap-1.5 whitespace-nowrap bg-foreground text-background hover:bg-foreground/90"
            >
              Pago
              <Plus className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </Tip>
          <Tip label={disabledTitle ?? 'Registrar promesa de pago'} disabled={disabled}>
            <Button
              type="button"
              size="sm"
              onClick={onRegistrarPromesa}
              disabled={disabled}
              className="gap-1.5 whitespace-nowrap bg-foreground text-background hover:bg-foreground/90"
            >
              Promesa
              <Plus className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </Tip>
        </div>
      </div>

      <div className="overscroll-contain lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        {comprobantes.length === 0 ? (
          <div className="rounded-xl border px-3 py-6 text-center text-sm text-muted-foreground">
            Sin pagos registrados.
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {comprobantes.map((c) => (
              <ComprobanteCard
                key={c.id}
                comp={c}
                moneda={moneda}
                homeCurrency={homeCurrency}
                onVer={() => onVerComprobante(c)}
                onAnular={() => onAnularComprobante(c)}
                onCargar={() => onCargarComprobante(c)}
                onEditar={() => onEditarPromesa(c)}
                previaEstado={previaEstadoByComprobante?.get(c.id ?? '')}
                previaMotivoRechazo={previaMotivoRechazoByComprobante?.get(c.id ?? '')}
                recargoMp={recargoMp}
                echeq={echeqByComprobante?.get(c.id ?? '')}
                onSolicitarPrevia={
                  onSolicitarFacturacionPrevia
                    ? () => onSolicitarFacturacionPrevia(c)
                    : undefined
                }
                readOnly={readOnly}
              />
            ))}
          </ul>
        )}
      </div>

      {summary && (
        <div className="flex shrink-0 flex-col gap-2 border-t pt-3">
          {/* Cobranza progress — mirrors the medios "Cobertura" pill
              styling but measures real cobro state (Pagado / Total).
              Lives at the bottom of the carga card so the right
              column visually balances with the left stack. */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Cobranza
            </span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  'h-full rounded-full transition-[width] duration-200',
                  progressPct != null && progressPct >= 100
                    ? 'bg-emerald-500'
                    : 'bg-amber-500',
                )}
                style={{ width: `${progressPct ?? 0}%` }}
              />
            </div>
            <span
              className={cn(
                'text-xs font-semibold tabular-nums',
                progressPct != null && progressPct >= 100
                  ? 'text-emerald-700'
                  : 'text-amber-700',
              )}
            >
              {progressPct ?? 0}%
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 2xl:grid-cols-4">
            <SummaryPill
              label="Disp. a Facturar"
              value={summary.disponibleAFacturar}
              moneda={moneda}
            />
            <SummaryPill label="Pagado" value={summary.pagado} moneda={moneda} />
            <SummaryPill
              label="Pendiente"
              value={summary.pendiente}
              moneda={moneda}
              tone={Math.abs(summary.pendiente) < 0.01 ? 'neutral' : 'amber'}
            />
            <SummaryPill label="Total" value={summary.total} moneda={moneda} />
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryPill({
  label,
  value,
  moneda,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  moneda: Moneda;
  tone?: 'neutral' | 'amber';
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 rounded-xl border bg-secondary px-3 py-2 leading-tight',
        tone === 'amber' && 'border-amber-200 bg-amber-50',
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'text-sm font-semibold tabular-nums text-foreground',
          tone === 'amber' && 'text-amber-800',
        )}
      >
        {formatMoney(value, moneda)}
      </span>
    </div>
  );
}

function ComprobanteCard({
  comp,
  moneda,
  homeCurrency,
  onVer,
  onAnular,
  onCargar,
  onEditar,
  onSolicitarPrevia,
  previaEstado,
  previaMotivoRechazo,
  recargoMp,
  echeq,
  readOnly,
}: {
  comp: ComprobantePagoItem;
  moneda: Moneda;
  homeCurrency: Moneda;
  onVer: () => void;
  onAnular: () => void;
  onCargar: () => void;
  onEditar: () => void;
  onSolicitarPrevia?: () => void;
  previaEstado?: string;
  previaMotivoRechazo?: string;
  recargoMp?: { caja: string; cuotas: number; pct: number } | null;
  echeq?: Echeq;
  readOnly: boolean;
}) {
  const f = comp.fields;
  const status = f.Status_P ?? '—';
  const isPromesa = status === 'Pendiente de Cobro';
  // Etiqueta de Facturación Previa. El flag del comprobante sólo se prende al
  // aprobar, así que para "pendiente" y "rechazada" miramos el estado de la
  // solicitud — si no, un rechazo no deja ningún rastro visible y el operador
  // no sabe si nunca se pidió o se la negaron.
  const previaAprobada = f.FacturacionPrevia_CP === 'SI';
  const previaTag: { label: string; className: string; title: string } | null =
    previaAprobada || previaEstado === 'Aprobado'
      ? {
          // Verde = autorizada. El cobro ya puede facturarse sin haber entrado.
          label: 'Factura Anticipada',
          className: 'bg-emerald-100 text-emerald-700',
          title: 'Autorizado a facturar antes del cobro',
        }
      : previaEstado === 'Pendiente'
        ? {
            // Rojo, no ámbar: tiene que saltar a la vista que la factura NO
            // está habilitada todavía y que hay un admin trabado en el medio.
            label: 'Factura Anticipada - Pend. Aprobación',
            className: 'bg-rose-100 text-rose-700',
            title:
              'Factura anticipada esperando la aprobación de un administrador: todavía no se puede emitir',
          }
        : previaEstado === 'Rechazado'
          ? {
              // Neutro: la solicitud murió y el cobro sigue el curso normal.
              // En rojo se confundiría con "pendiente", que sí requiere acción.
              label: 'Factura Anticipada - Rechazada',
              className: 'bg-slate-200 text-slate-700',
              title: previaMotivoRechazo
                ? `Rechazada: ${previaMotivoRechazo}`
                : 'La factura anticipada fue rechazada: este cobro se factura después de cobrarse, como siempre',
            }
          : null;
  // Un comprobante ya facturado es inmutable (el server lo rechaza con 409):
  // sólo una Nota de Crédito cambia algo autorizado ante ARCA. Con Facturación
  // Previa esto pasa mientras la fila sigue en un estado "editable", así que
  // el botón hay que esconderlo explícitamente.
  const facturaEmitida = f.Facturado_CP === 'SI';
  // Se puede pedir facturación previa mientras el cobro no esté confirmado ni
  // facturado, lleve factura y no la tenga ya aprobada. Deliberadamente NO usa
  // `isPromesa`: también aplica al pago cargado esperando confirmación.
  const puedePedirPrevia =
    !readOnly &&
    Boolean(onSolicitarPrevia) &&
    !previaAprobada &&
    // Ya hay una solicitud esperando: el server la devuelve idempotente, pero
    // no tiene sentido invitar al click. Tras un RECHAZO sí se puede volver a
    // pedir (mismo criterio que factura%, que relajó el bloqueo en 2026-05-19).
    previaEstado !== 'Pendiente' &&
    f.ContieneFactura_CP === 'SI' &&
    f.Facturado_CP !== 'SI' &&
    (status === 'Pendiente de Cobro' || status === 'Pendiente de Confirmacion');
  // Fuera de Tesorería el cobro se muestra con su caja base: "Efectivo Pesos N"
  // es un detalle interno de caja (lib/cajasEfectivo).
  const medio = cajaBase(f.MedioPago_CP ?? '');
  const isUsdMedio =
    cajaBase(medio) === 'Efectivo Dolar' ||
    medio === 'Transferencia Galicia Dolar' ||
    medio === 'Transferencia Dolar';
  // The medio's native currency: USD for the dollar medios, otherwise the
  // active country's home currency. When it differs from the venta
  // currency the pago is cross-currency, so `Monto_CP` holds the native
  // amount and `Pesos_CP` the venta-currency equivalent. We headline the
  // venta-currency amount (comparable to the totals) and surface the
  // native amount as a secondary label — symmetric for an ARS pago on a
  // USD venta (where Monto_CP=ARS and Pesos_CP=USD).
  const medioMoneda: Moneda = isUsdMedio ? 'USD' : homeCurrency;
  const crossCurrency = medioMoneda !== moneda;
  const montoNativo = parseMoney(f.Monto_CP);
  const montoConvertido = parseMoney(f.Pesos_CP);
  const displayAmount = crossCurrency
    ? montoConvertido ?? montoNativo ?? 0
    : montoNativo ?? 0;
  // Only show the secondary native-currency line for a genuine
  // cross-currency pair (both amounts present); a legacy row missing
  // Pesos_CP would otherwise repeat the same number under two labels.
  const showNativeAmount =
    crossCurrency && montoConvertido != null && montoNativo != null;
  // Dos motivos distintos para no poder anular, y se resuelven distinto:
  //   - readOnly  → esta vista es de sólo lectura (se llegó desde
  //     Administración, o el perfil no escribe acá). El botón NO se muestra,
  //     igual que cargar y editar, que ya se esconden solos.
  //   - el estado → el comprobante está confirmado o ya anulado. Ahí sí lo
  //     mostramos deshabilitado, porque el motivo es del comprobante y el
  //     usuario podría cambiarlo.
  const anulableSegunEstado = status !== 'Confirmado' && status !== 'Anulado';
  return (
    <li
      className={cn(
        'flex flex-col gap-2 rounded-xl border bg-card px-3 py-2.5 shadow-sm',
        status === 'Anulado' && 'opacity-60',
      )}
    >
      {/* Top row: método (bold, left) + status pill (top-right
          corner, full label). Status sits up here because it's
          primary scan info just like method. */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="font-semibold text-foreground">{medio || '—'}</span>
          {recargoMp && medio === cajaBase(recargoMp.caja) && (
            <div className="text-xs text-muted-foreground">
              {recargoMp.cuotas} cuotas · recargo MP +{fmtPct(recargoMp.pct)}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {previaTag && (
            <Tip label={previaTag.title}>
              <span
                // Misma forma que la píldora de estado (PILL_BASE) para que las
                // dos se lean como parte del mismo grupo.
                //
                // `whitespace-normal` sólo en mobile: "Factura Anticipada - Pend.
                // Aprobación" mide ~245px con el nowrap de PILL_BASE, y como una
                // píldora nowrap no puede achicarse, fijaba el ancho mínimo de la
                // fila por encima del ancho de la tarjeta (~300px a 360px de
                // viewport) y la desbordaba. Dejándola cortar, el mínimo pasa a
                // ser la palabra más larga. De `sm` para arriba entra en una
                // línea y queda idéntica a la píldora de estado.
                className={cn(
                  PILL_BASE,
                  'normal-case',
                  'whitespace-normal text-center sm:whitespace-nowrap',
                  previaTag.className,
                )}
              >
                {previaTag.label}
              </span>
            </Tip>
          )}
          {echeq && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn(PILL_BASE, 'normal-case cursor-help', ECHEQ_PILL[echeq.estado])}>
                  E-cheq {echeq.nroCheque} · {ESTADO_ECHEQ_LABEL[echeq.estado]}
                </span>
              </TooltipTrigger>
              <TooltipContent className="space-y-0.5">
                <p className="font-medium">{echeq.bancoEmisor}</p>
                <p>CUIT {echeq.cuitLibrador}</p>
                <p>Fecha de pago {echeq.fechaPago.split('-').reverse().join('/')}</p>
                {echeq.cajaAcreditacion && <p>Acreditado en {echeq.cajaAcreditacion}</p>}
                {echeq.motivoRechazo && <p>Motivo: {echeq.motivoRechazo}</p>}
              </TooltipContent>
            </Tooltip>
          )}
          <StatusCPBadge status={status} full />
        </div>
      </div>

      {/* Meta strip: factura indicator + dates, all muted so they
          read as secondary context. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>
          Factura:{' '}
          <span className="font-medium text-foreground">
            {f.ContieneFactura_CP === 'SI' ? 'Sí' : 'No'}
          </span>
        </span>
        <span>·</span>
        <span>
          Cobro{' '}
          <span className="font-medium text-foreground">{f.FechaCobro_CP ?? '—'}</span>
        </span>
        <span>·</span>
        <span>
          Pago{' '}
          <span className="font-medium text-foreground">{f.FechaPago_CP ?? '—'}</span>
        </span>
        <span>·</span>
        {/* Cuándo se cargó (fecha y hora reales de alta). */}
        <span>
          Cargado{' '}
          <span className="font-medium text-foreground">{f.FechaHoraCarga_CP ?? f.Fecha_CP ?? '—'}</span>
        </span>
      </div>

      {/* Divider between content and action footer — gives the
          card a clear two-zone structure. */}
      <div className="border-t" />

      {/* Footer row: monto on the left as the headline number,
          actions clustered on the right. Status now lives in the
          top-right corner; the divider above marks the data /
          action split. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col">
          <MoneyCell amount={displayAmount} moneda={moneda} />
          {showNativeAmount && (
            <span className="text-xs text-muted-foreground">
              {formatMoney(montoNativo, medioMoneda)}{' '}
              <span className="font-medium">{medioMoneda}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isPromesa && !readOnly ? (
            <>
              <Tip label="Subir comprobante de pago">
                <button
                  type="button"
                  onClick={onCargar}
                  aria-label="Subir comprobante de pago"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                >
                  <CircleDollarSign className="h-4 w-4" aria-hidden />
                </button>
              </Tip>
              {!facturaEmitida && (
                <Tip label="Editar pago">
                  <button
                    type="button"
                    onClick={onEditar}
                    aria-label="Editar pago"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </button>
                </Tip>
              )}
            </>
          ) : (
            <>
              <Tip label="Ver comprobante">
                <button
                  type="button"
                  onClick={onVer}
                  aria-label="Ver comprobante"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  <Eye className="h-4 w-4" aria-hidden />
                </button>
              </Tip>
              {status === 'Pendiente de Confirmacion' && !readOnly && !facturaEmitida && !echeq && (
                <Tip label="Editar pago">
                  <button
                    type="button"
                    onClick={onEditar}
                    aria-label="Editar pago"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </button>
                </Tip>
              )}
            </>
          )}
          {puedePedirPrevia && (
            <Tip
              label={
                previaEstado === 'Rechazado'
                  ? 'Volver a pedir la Factura Anticipada (ya te la rechazaron una vez)'
                  : 'Solicitar Factura Anticipada (emitir la factura antes de cobrar)'
              }
            >
              <button
                type="button"
                onClick={onSolicitarPrevia}
                aria-label={
                  previaEstado === 'Rechazado'
                    ? 'Volver a pedir Factura Anticipada'
                    : 'Solicitar Factura Anticipada'
                }
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100"
              >
                <ReceiptText className="h-4 w-4" aria-hidden />
              </button>
            </Tip>
          )}
          {!readOnly && (
            <Tip
              disabled={!anulableSegunEstado}
              label={
                !anulableSegunEstado
                  ? `No se puede anular un comprobante ${
                      status === 'Confirmado' ? 'confirmado' : 'ya anulado'
                    }`
                  : f.Facturado_CP === 'SI'
                    ? // Con factura emitida el diálogo pide la anulación, no la hace.
                      'Solicitar anulación'
                    : 'Anular comprobante'
              }
            >
              <button
                type="button"
                onClick={onAnular}
                disabled={!anulableSegunEstado}
                aria-label={
                  f.Facturado_CP === 'SI' ? 'Solicitar anulación' : 'Anular comprobante'
                }
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </Tip>
          )}
        </div>
      </div>
    </li>
  );
}

const ECHEQ_PILL: Record<Echeq['estado'], string> = {
  'Pendiente Autorizacion': 'bg-rose-100 text-rose-700',
  Autorizado: 'bg-sky-100 text-sky-700',
  Acreditado: 'bg-emerald-100 text-emerald-700',
  Rechazado: 'bg-slate-200 text-slate-700',
  'Rechazado Banco': 'bg-slate-200 text-slate-700',
  Anulado: 'bg-slate-200 text-slate-700',
};

/** Tooltip del sistema (Radix) en lugar del `title` nativo. Un botón
 *  deshabilitado no recibe eventos de mouse, así que en ese caso el
 *  disparador es un span que lo envuelve. */
function Tip({ label, disabled, children }: { label: string; disabled?: boolean; children: ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {disabled ? <span tabIndex={0} className="inline-flex cursor-not-allowed [&>*]:pointer-events-none">{children}</span> : children}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
